import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnInit} from '@angular/core';
import {NbDialogRef} from '@nebular/theme';
import {AbstractControl, FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {HelpersService} from '@core/utils/helpers.service';
import {ApiType, S3Service} from '@core/interfaces/common/s3';
import {HttpEvent, HttpEventType} from '@angular/common/http';
import {MediaFieldResultDto} from '@core/interfaces/engin/maintenance-planning/form-visualization';
import {mergeMap, tap} from 'rxjs/operators';

@Component({
    selector: 'ngx-form-field-media-dialog',
    templateUrl: './upload-media-dialog.component.html',
    styleUrls: ['./upload-media-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UploadMediaDialogComponent implements OnInit {
    /* Enable image and/or video media */
    @Input() enableImage: boolean = true;
    @Input() enableVideo: boolean = false;
    @Input() s3service: S3Service;
    @Input() fieldId: number;
    @Input() fieldCode: string;
    imageFormats: string[] = ['jpeg', 'jpg', 'png'];
    videoFormats: string[] = ['mov', 'mp4'];
    fileType: string = '';

    acceptedFileTypes: string[] = [];
    completedFileList: MediaFieldResultDto[] = [];

    fileUploadForm: FormGroup = this.fb.group({
        fileList: this.fb.array([], Validators.required),
    });

    constructor(
        protected ref: NbDialogRef<UploadMediaDialogComponent>,
        private fb: FormBuilder,
        private changeDetectorRef: ChangeDetectorRef,
        private helpersService: HelpersService,
    ) {}

    ngOnInit(): void {
        if (this.enableImage && !this.enableVideo) {
            this.acceptedFileTypes.push(...this.imageFormats);
            this.fileType = 'Images';
        }

        if (this.enableVideo && !this.enableImage) {
            this.fileType = 'Videos';
            this.acceptedFileTypes.push(...this.videoFormats);
        }

        if (this.enableImage && this.enableVideo) {
            this.fileType = 'Videos/Images';
            this.acceptedFileTypes.push(...this.videoFormats, ...this.imageFormats);
        }
    }

    getFileControl(fileName: string): AbstractControl {
        let foundControl = null;
        this.fileList.controls.forEach((fileControl) => {
            if (fileControl.value.name === fileName) {
                foundControl = fileControl;
            }
        });
        return foundControl;
    }

    onFileAdded(file) {
        const fr = new FileReader();
        fr.readAsDataURL(file.file);
        fr.onload = (e) => {
            file = {
                ...file,
                url: e.target.result,
                progress: 0,
                status: 'STARTED',
            };
            this.fileList.push(this.createItem(file));
            this.changeDetectorRef.detectChanges();
            this.s3service
                .getPresignedUrl(file.name, file.fileFormat, ApiType.MAINTENANCE_PLANNING)
                .pipe(
                    mergeMap((url) => {
                        return this.s3service.uploadToPresignedUrl(url.response.url, file.file).pipe(
                            tap((event: HttpEvent<any>) => {
                                let control = this.getFileControl(file.name);
                                switch (event.type) {
                                    case HttpEventType.UploadProgress:
                                        if (control) {
                                            control.value.progress = Math.round((event.loaded * 100) / event.total);
                                        }

                                        // we don't always get the response that the file is complete
                                        // so we check if progress is 100 and then call the shared
                                        // method to mark the control complete and push completedFile
                                        if (control.value.progress === 100) {
                                            this.uploadFileComplete(control, file);
                                        }
                                        this.changeDetectorRef.markForCheck();
                                        break;
                                    case HttpEventType.Response:
                                        this.uploadFileComplete(control, file);
                                        break;
                                }
                            }),
                        );
                    }),
                )
                .subscribe();
        };
    }

    uploadFileComplete(control: AbstractControl, file: any): void {
        if (control) {
            control.value.status = 'UPLOADED';
        }
        /*
         * Do not change file.url (the local image location) -> url.response.url (the
         * pre-signed upload url for the new image). This is not the link to get the image.
         * - See: ENGIN-4446
         */
        const media: MediaFieldResultDto = {
            fieldId: this.fieldId,
            fileKey: `inspection-content/${file.file.name}`,
            fileName: file.file.name,
            fileFormat: file.fileFormat,
            fileSize: this.helpersService.parseFileSize(file.size + ' bytes'),
            formattedSize: file.size,
            url: file.url,
            fieldCode: this.fieldCode,
        };

        if (
            this.completedFileList.findIndex((completedFile: MediaFieldResultDto) => {
                return completedFile.fileKey === media.fileKey;
            }) === -1
        ) {
            this.completedFileList.push(media);
        }
    }

    createItem = (file): FormGroup => {
        return this.fb.group({
            name: file.name,
            status: file.status,
            fileFormat: file.fileFormat,
            dataOperationType: 'FULL_REFRESH',
            fileLoadType: 'SNAPSHOT',
            tableArray: [],
            progress: 0,
            size: this.helpersService.formatFileSize(file.size),
            file: file.file,
            url: file.url,
        });
    };
    get fileList() {
        return this.fileUploadForm.get('fileList') as FormArray;
    }

    removeFile(indexToRemove: number) {
        // get handle on file name before removing it
        const fileName = this.fileList.controls[indexToRemove].value.name;

        // remove this instance of file from the list
        this.fileList.removeAt(indexToRemove);

        // remove the completed file if it has finished uploading so it doesn't return onClose
        const completedFileIndex = this.completedFileList.findIndex((file: MediaFieldResultDto) => {
            return file.fileName === fileName;
        });

        if (completedFileIndex > -1) {
            this.completedFileList.splice(completedFileIndex, 1);
        }
    }

    save() {
        this.ref.close({contents: this.completedFileList});
    }
    cancel() {
        this.ref.close();
    }
    public mediaIsImage(fileFormat: string): boolean {
        return this.imageFormats.includes(fileFormat.toLowerCase());
    }
}
